import React, { useState } from "react";
import { validateAllowedText } from "./utils/contentModeration";
import { useNavigate } from "react-router-dom";
import config from "./config";
import { applyAccessTokenFromAuthResponse } from "./services/authSession";
import { resendVerificationEmail } from "./services/verificationApi";
import { trackSignup, trackLogin } from "./utils/analytics";

function getTokenFromUrl() {
  const params = new URLSearchParams(window.location.search);
  return params.get("token") || "";
}

function Footer({ onBack }) {
  const navigate = useNavigate();
  
  return (
    <footer
      style={{
        textAlign: "center",
        color: "#94a3b8",
        fontSize: "0.98em",
        marginTop: 40,
        padding: 20,
      }}
    >

      <div>
        <button
          onClick={() => {
            if (onBack) {
              onBack("terms");
            } else {
              navigate("/terms");
            }
          }}
          style={{
            background: "none",
            border: "none",
            color: "#2563eb",
            textDecoration: "underline",
            cursor: "pointer",
            fontSize: "0.9em",
            marginRight: 16,
          }}
        >
          Terms of Service
        </button>
        <button
          onClick={() => {
            if (onBack) {
              onBack("privacy");
            } else {
              navigate("/privacy");
            }
          }}
          style={{
            background: "none",
            border: "none",
            color: "#2563eb",
            textDecoration: "underline",
            cursor: "pointer",
            fontSize: "0.9em",
          }}
        >
          Privacy Policy
        </button>
      </div>
    </footer>
  );
}

function ResetPassword({ onBack }) {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const token = getTokenFromUrl();

  const handleReset = async (e) => {
    e.preventDefault();
    if (!newPassword || !confirmPassword) {
      setMsg("Please enter and confirm your new password.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setMsg("Passwords do not match.");
      return;
    }
    setLoading(true);
    setMsg("");
    try {
      const response = await fetch(`${config.API_URL}/users/reset_password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include", // Include cookies
        body: JSON.stringify({ token, new_password: newPassword })
      });
      const data = await response.json();
      if (response.ok) {
        setMsg("Password reset successful! You may now log in.");
      } else {
        // Handle rate limit errors
        if (response.status === 429) {
          setMsg("Too many reset attempts. Please wait a minute before trying again.");
        } else {
          setMsg(data.detail || "Reset failed. Try again or request a new link.");
        }
      }
    } catch (err) {
      setMsg("An error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ffb-bg">
      <header className="ffb-header">
        <h1 className="ffb-header-title">🏈 Fantasy Football Classic™ 🏈</h1>
      </header>
      <div style={{ padding: 32, maxWidth: 400, margin: "0 auto" }}>
        <button
          onClick={onBack}
          className="ffb-btn"
          style={{ marginBottom: 24, padding: "8px 20px", fontSize: 16, borderRadius: 4 }}
        >
          Back
        </button>
        <div style={{ background: "#fff", padding: 32, borderRadius: 16, boxShadow: "0 2px 16px rgba(30,58,92,0.10)" }}>
          <h2 style={{ textAlign: "center", marginBottom: 24, color: "#1e3a8a" }}>Set New Password</h2>
          <form onSubmit={handleReset}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 600, color: "#334155" }}>
                New Password:
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                style={{ width: "100%", padding: 12, borderRadius: 8, border: "1px solid #d1d5db", fontSize: 16 }}
                placeholder="Enter new password"
                disabled={loading}
              />
            </div>
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 600, color: "#334155" }}>
                Confirm Password:
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                style={{ width: "100%", padding: 12, borderRadius: 8, border: "1px solid #d1d5db", fontSize: 16 }}
                placeholder="Confirm new password"
                disabled={loading}
              />
            </div>
            {msg && (
              <div style={{ color: msg.includes("successful") ? "#16a34a" : "#dc2626", marginBottom: 16, padding: 12, background: msg.includes("successful") ? "#f0fdf4" : "#fef2f2", borderRadius: 8, border: msg.includes("successful") ? "1px solid #bbf7d0" : "1px solid #fecaca" }}>
                {msg}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="ffb-btn"
              style={{ width: "100%", padding: 12, fontSize: 16, fontWeight: 600, marginBottom: 16 }}
            >
              {loading ? "Please wait..." : "Set New Password"}
            </button>
          </form>
        </div>
      </div>
      <Footer onBack={onBack} />
    </div>
  );
}

export default function Login({ onLogin, onBack }) {
  const navigate = useNavigate();
  const [isRegistering, setIsRegistering] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetMsg, setResetMsg] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [email, setEmail] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [showVerifyMsg, setShowVerifyMsg] = useState(false);
  const [resendMsg, setResendMsg] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [showResendVerification, setShowResendVerification] = useState(false);

  const handleResendVerification = async (emailToUse) => {
    const targetEmail = (emailToUse || email || "").trim();
    if (!targetEmail) {
      setResendMsg("Enter the email address you used to register.");
      return;
    }
    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(targetEmail)) {
      setResendMsg("Please enter a valid email address.");
      return;
    }
    setResendLoading(true);
    setResendMsg("");
    try {
      const message = await resendVerificationEmail(targetEmail);
      setResendMsg(message);
    } catch (err) {
      setResendMsg(err.message || "Could not resend verification email.");
    } finally {
      setResendLoading(false);
    }
  };

  const validateInputs = () => {
    if (isRegistering) {
      if (!username.trim() || !password.trim() || !email.trim()) {
        setError("Please enter username, email, and password");
        return false;
      }
      if (!agreed) {
        setError("You must agree to the Terms of Service and Privacy Policy");
        return false;
      }
      if (!ageConfirmed) {
        setError("You must confirm that you are at least 13 years old");
        return false;
      }
      // Frontend validation for registration
      if (username.length < 3) {
        setError("Username must be at least 3 characters long");
        return false;
      }
      if (username.length > 30) {
        setError("Username must be less than 30 characters");
        return false;
      }
      if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
        setError("Username can only contain letters, numbers, underscores, and hyphens");
        return false;
      }
      try {
        validateAllowedText(username, { fieldName: "Username" });
      } catch (err) {
        setError(err.message);
        return false;
      }
      if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)) {
        setError("Please enter a valid email address");
        return false;
      }
      if (password.length < 8) {
        setError("Password must be at least 8 characters long");
        return false;
      }
      if (password.length > 128) {
        setError("Password is too long");
        return false;
      }
      if (['password', '123456', 'qwerty', 'admin'].includes(password.toLowerCase())) {
        setError("Password is too common. Please choose a stronger password");
        return false;
      }
    } else {
      if (!username.trim() || !password.trim()) {
        setError("Please enter both username and password");
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateInputs()) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      const endpoint = isRegistering ? "/users/register" : "/users/login";
      
      // Get invite token from URL or localStorage if user is signing up
      let inviteToken = null;
      if (isRegistering) {
        const urlParams = new URLSearchParams(window.location.search);
        inviteToken = urlParams.get('token') || localStorage.getItem('pendingInviteToken');
      }
      
      const body = isRegistering
        ? JSON.stringify({ username, email, password, invite_token: inviteToken })
        : JSON.stringify({ username, password });
      const response = await fetch(`${config.API_URL}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include", // Include cookies
        body
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // Handle rate limit errors
        if (response.status === 429) {
          setError("Too many attempts. Please wait a minute before trying again.");
          return;
        }
        
        // Show a special message if not verified
        if (!isRegistering && errorData.detail && errorData.detail.toLowerCase().includes("verify")) {
          setError("Please verify your email before logging in. Check your inbox or resend a new link below.");
          setShowResendVerification(true);
        } else {
          setError(errorData.detail || "Authentication failed");
        }
        return;
      }

      const data = await response.json();
      if (!isRegistering) {
        applyAccessTokenFromAuthResponse(data);
      }
      if (isRegistering) {
        trackSignup(); // Track user signup
        setShowVerifyMsg(true);
        setLoading(false);
        return;
      }
      // User data is now managed by cookies, no need to store in localStorage
      trackLogin(); // Track user login
      onLogin(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetRequest = async (e) => {
    e.preventDefault();
    if (!resetEmail.trim()) {
      setResetMsg("Please enter your email address.");
      return;
    }
    setLoading(true);
    setResetMsg("");
    try {
      const response = await fetch(`${config.API_URL}/users/request_password_reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include", // Include cookies
        body: JSON.stringify({ email: resetEmail })
      });
      const data = await response.json();
      
      // Handle rate limit errors
      if (response.status === 429) {
        setResetMsg("Too many reset attempts. Please wait a minute before trying again.");
      } else {
        setResetMsg(data.message || "If this email exists, a reset link will be sent.");
      }
    } catch (err) {
      setResetMsg("An error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Show reset password form if token is present in URL
  const tokenInUrl = typeof window !== "undefined" && getTokenFromUrl();
  if (tokenInUrl) {
    return <ResetPassword onBack={onBack} />;
  }

  // Show verify email message after registration
  if (showVerifyMsg) {
    return (
      <div className="ffb-bg">
        <header className="ffb-header">
          <h1 className="ffb-header-title">🏈 Fantasy Football Classic™ 🏈</h1>
        </header>
        <div style={{ padding: 32, maxWidth: 400, margin: "0 auto" }}>
          <div style={{ background: "#fff", padding: 32, borderRadius: 16, boxShadow: "0 2px 16px rgba(30,58,92,0.10)" }}>
            <h2 style={{ textAlign: "center", marginBottom: 24, color: "#1e3a8a" }}>Check Your Email</h2>
            <p style={{ fontSize: 16, color: "#334155", marginBottom: 16 }}>
              Your account was created! Please check your email for a verification link before logging in.
            </p>
            <p style={{ fontSize: 14, color: "#64748b", marginBottom: 16 }}>
              Email sent to: <strong>{email}</strong>
            </p>
            {resendMsg && (
              <div style={{ color: "#16a34a", marginBottom: 16, padding: 12, background: "#f0fdf4", borderRadius: 8, border: "1px solid #bbf7d0", fontSize: 14 }}>
                {resendMsg}
              </div>
            )}
            <button
              type="button"
              className="ffb-btn"
              style={{ width: "100%", marginBottom: 12 }}
              disabled={resendLoading}
              onClick={() => handleResendVerification(email)}
            >
              {resendLoading ? "Sending..." : "Resend verification email"}
            </button>
            <button className="ffb-btn ffb-btn-secondary" style={{ width: "100%" }} onClick={() => { setShowVerifyMsg(false); setIsRegistering(false); setResendMsg(""); }}>
              Back to Login
            </button>
          </div>
        </div>
        <Footer onBack={onBack} />
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '32px',
          maxWidth: '500px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }}
      >
        <div className="ffb-bg">
          <header className="ffb-header">
            <h1 className="ffb-header-title">🏈 Fantasy Football Classic™ 🏈</h1>
          </header>
          <div style={{ padding: 32, maxWidth: 400, margin: "0 auto" }}>
            <button
              onClick={onBack}
              className="ffb-btn"
              style={{ marginBottom: 24, padding: "8px 20px", fontSize: 16, borderRadius: 4 }}
            >
              Back
            </button>
            
            <div style={{ 
              background: "#fff", 
              padding: 32, 
              borderRadius: 16, 
              boxShadow: "0 2px 16px rgba(30,58,92,0.10)" 
            }}>
              <h2 style={{ textAlign: "center", marginBottom: 24, color: "#1e3a8a" }}>
                {showReset ? "Reset Password" : (isRegistering ? "Create Account" : "Sign In")}
              </h2>
              {showReset ? (
                <form onSubmit={handleResetRequest}>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: "block", marginBottom: 8, fontWeight: 600, color: "#334155" }}>
                      Email:
                    </label>
                    <input
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      style={{
                        width: "100%",
                        padding: 12,
                        borderRadius: 8,
                        border: "1px solid #d1d5db",
                        fontSize: 16
                      }}
                      placeholder="Enter your email address"
                      disabled={loading}
                    />
                  </div>
                  {resetMsg && (
                    <div style={{ 
                      color: "#2563eb", 
                      marginBottom: 16, 
                      padding: 12, 
                      background: "#eff6ff", 
                      borderRadius: 8,
                      border: "1px solid #bae6fd"
                    }}>
                      {resetMsg}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={loading}
                    className="ffb-btn"
                    style={{ 
                      width: "100%", 
                      padding: 12, 
                      fontSize: 16, 
                      fontWeight: 600,
                      marginBottom: 16
                    }}
                  >
                    {loading ? "Please wait..." : "Request Password Reset"}
                  </button>
                  <div style={{ textAlign: "center" }}>
                    <button
                      type="button"
                      onClick={() => setShowReset(false)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#2563eb",
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontSize: 14
                      }}
                    >
                      Back to {isRegistering ? "Register" : "Login"}
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: "block", marginBottom: 8, fontWeight: 600, color: "#334155" }}>
                      Username:
                    </label>
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      style={{
                        width: "100%",
                        padding: 12,
                        borderRadius: 8,
                        border: "1px solid #d1d5db",
                        fontSize: 16
                      }}
                      placeholder="Enter your username"
                      disabled={loading}
                    />
                  </div>
                  
                  {isRegistering && (
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: "block", marginBottom: 8, fontWeight: 600, color: "#334155" }}>
                        Email:
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        style={{
                          width: "100%",
                          padding: 12,
                          borderRadius: 8,
                          border: "1px solid #d1d5db",
                          fontSize: 16
                        }}
                        placeholder="Enter your email address"
                        disabled={loading}
                      />
                    </div>
                  )}
                  
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: "block", marginBottom: 8, fontWeight: 600, color: "#334155" }}>
                      Password:
                    </label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      style={{
                        width: "100%",
                        padding: 12,
                        borderRadius: 8,
                        border: "1px solid #d1d5db",
                        fontSize: 16
                      }}
                      placeholder="Enter your password"
                      disabled={loading}
                    />
                  </div>

                  {isRegistering && (
                    <>
                      <div style={{ marginBottom: 16 }}>
                        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: "#334155" }}>
                          <input
                            type="checkbox"
                            checked={agreed}
                            onChange={(e) => setAgreed(e.target.checked)}
                            style={{ margin: 0 }}
                          />
                          I agree to the{" "}
                          <button
                            type="button"
                            onClick={() => {
                              if (onBack) {
                                onBack("terms");
                              } else {
                                navigate("/terms");
                              }
                            }}
                            style={{
                              background: "none",
                              border: "none",
                              color: "#2563eb",
                              textDecoration: "underline",
                              cursor: "pointer",
                              padding: 0,
                              fontSize: 14
                            }}
                          >
                            Terms of Service
                          </button>{" "}
                          and{" "}
                          <button
                            type="button"
                            onClick={() => {
                              if (onBack) {
                                onBack("privacy");
                              } else {
                                navigate("/privacy");
                              }
                            }}
                            style={{
                              background: "none",
                              border: "none",
                              color: "#2563eb",
                              textDecoration: "underline",
                              cursor: "pointer",
                              padding: 0,
                              fontSize: 14
                            }}
                          >
                            Privacy Policy
                          </button>
                        </label>
                      </div>
                      <div style={{ marginBottom: 16 }}>
                        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: "#334155" }}>
                          <input
                            type="checkbox"
                            checked={ageConfirmed}
                            onChange={(e) => setAgeConfirmed(e.target.checked)}
                            style={{ margin: 0 }}
                          />
                          I confirm that I am at least 13 years old
                        </label>
                      </div>
                    </>
                  )}

                  {error && (
                    <div style={{ 
                      color: "#dc2626", 
                      marginBottom: 16, 
                      padding: 12, 
                      background: "#fef2f2", 
                      borderRadius: 8,
                      border: "1px solid #fecaca"
                    }}>
                      {error}
                    </div>
                  )}

                  {showResendVerification && !isRegistering && (
                    <div style={{ marginBottom: 16, padding: 12, background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                      <p style={{ fontSize: 14, color: "#334155", marginBottom: 8, fontWeight: 600 }}>
                        Resend verification email
                      </p>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        style={{
                          width: "100%",
                          padding: 12,
                          borderRadius: 8,
                          border: "1px solid #d1d5db",
                          fontSize: 16,
                          marginBottom: 8,
                          boxSizing: "border-box",
                        }}
                        placeholder="Email you registered with"
                        disabled={resendLoading}
                      />
                      {resendMsg && (
                        <div style={{ color: "#16a34a", marginBottom: 8, fontSize: 14 }}>
                          {resendMsg}
                        </div>
                      )}
                      <button
                        type="button"
                        className="ffb-btn"
                        style={{ width: "100%" }}
                        disabled={resendLoading}
                        onClick={() => handleResendVerification(email)}
                      >
                        {resendLoading ? "Sending..." : "Send new verification link"}
                      </button>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="ffb-btn"
                    style={{ 
                      width: "100%", 
                      padding: 12, 
                      fontSize: 16, 
                      fontWeight: 600,
                      marginBottom: 16
                    }}
                  >
                    {loading ? "Please wait..." : (isRegistering ? "Create Account" : "Sign In")}
                  </button>

                  <div style={{ textAlign: "center", marginBottom: 16 }}>
                    <button
                      type="button"
                      onClick={() => setIsRegistering(!isRegistering)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#2563eb",
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontSize: 14
                      }}
                    >
                      {isRegistering ? "Already have an account? Sign In" : "Don't have an account? Create One"}
                    </button>
                  </div>

                  {!isRegistering && (
                    <div style={{ textAlign: "center" }}>
                      <button
                        type="button"
                        onClick={() => setShowReset(true)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#2563eb",
                          textDecoration: "underline",
                          cursor: "pointer",
                          fontSize: 14
                        }}
                      >
                        Forgot your password?
                      </button>
                      {!showResendVerification && (
                        <>
                          <span style={{ color: "#94a3b8", margin: "0 8px" }}>|</span>
                          <button
                            type="button"
                            onClick={() => { setShowResendVerification(true); setResendMsg(""); }}
                            style={{
                              background: "none",
                              border: "none",
                              color: "#2563eb",
                              textDecoration: "underline",
                              cursor: "pointer",
                              fontSize: 14
                            }}
                          >
                            Resend verification email
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </form>
              )}
            </div>
          </div>
          <Footer onBack={onBack} />
        </div>
      </div>
    </div>
  );
} 